import { Form, FormikProvider, useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { ModalBody, ModalHeader, Row } from 'reactstrap'
import * as Yup from 'yup'
import activities from '../../../../api/activities/hooks'
import { ActivityCreatePayload, ActivityUpdatePayload } from '../../../../api/activities/types'
import { PermissionsProps } from '../../../../api/globalTypes'
import { MODAL_TYPES } from '../../../../common/constants'
import { showModal } from '../../../../store/modal'
import AppPermissionsSwitcher from '../../../AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../AppPermissionsSwitcher/constants/actions'
import { EntityParams } from '../../../AppPermissionsSwitcher/EntityParamsContext'
import { DeleteButton, SaveButton } from '../../../Common/Buttons'
import RecursiveForm from '../../../RecursiveForm'
import { setDeleteCallback } from '../../DeleteModal/utils'
import { useActivityFormConfig, useActivityFormSelectOptions } from '../hooks'
import { ActivityModalData } from '../types'

interface FormContentProps {
    event: ActivityModalData
    isEdit: boolean
    parentId?: string
    detailView?: string
    params?: EntityParams
    onClose: () => void
}

const FormContent = ({ event, isEdit, parentId, detailView, params, onClose }: FormContentProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { DELETE_MODAL } = MODAL_TYPES

    const activityAgentId = event?.activityAgent?.[0]?.agent?.id

    const { formikInputs, formikConfig } = useActivityFormConfig(event)
    const { selectOptions, isPending: selectOptionsLoading } = useActivityFormSelectOptions(event, parentId, detailView, params)

    const { mutate: createActivity } = activities.CreateActivity.useMutation()
    const { mutate: updateActivity } = activities.UpdateActivity.useMutation()
    const { mutate: deleteActivity } = activities.DeleteActivity.useMutation()

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: formikConfig.initialValues ?? {},
        validationSchema: formikConfig.validationSchema ?? Yup.object({}),
        onSubmit: (values) => {
            if (isEdit) {
                updateActivity({ ...values, id: Number(event.id) } as ActivityUpdatePayload)
            } else {
                createActivity(values as ActivityCreatePayload)
            }
        },
    })

    const handleDelete = () => {
        const callbackId = 'delete-activity'
        const payload = {
            modalType: DELETE_MODAL,
            modalProps: { callbackId },
        }

        setDeleteCallback(callbackId, () => {
            deleteActivity(Number(event.id))
            onClose()
        })

        dispatch(showModal(payload))
    }

    return (
        <>
            <ModalHeader
                toggle={onClose}
                tag="h5"
                className={`p-3 modal-title${!isEdit ? ' bg-soft-add' : ''}`}
                style={isEdit && event?.backgroundColor ? { background: event.backgroundColor } : undefined}
            >
                {isEdit ? `${event.title ? event.title : t('modal.activity.editActivity')}` : t('app.common.addActivity')}
            </ModalHeader>
            <ModalBody>
                <FormikProvider value={formik}>
                    <Form onSubmit={formik.handleSubmit}>
                        <Row className="event-form">
                            <RecursiveForm
                                inputs={formikInputs.mainForm}
                                formik={formik}
                                selectOptions={selectOptions}
                                selectOptionsLoading={selectOptionsLoading}
                                config={undefined}
                                subform={undefined}
                                inputComponents={undefined}
                                fieldArrayDeleteComponent={undefined}
                            />
                        </Row>
                        <div className="hstack gap-2 justify-content-end">
                            {isEdit && (
                                <AppPermissionsSwitcher
                                    permission="ModulePermissions"
                                    activityAgentId={activityAgentId}
                                    module={'activities'}
                                    action={action.delete}
                                >
                                    {({ hasModulePermission }: PermissionsProps) =>
                                        hasModulePermission && <DeleteButton id="btn-delete-event" onClick={handleDelete} />
                                    }
                                </AppPermissionsSwitcher>
                            )}
                            <SaveButton />
                        </div>
                    </Form>
                </FormikProvider>
            </ModalBody>
        </>
    )
}

export default FormContent
